import { useCallback, useEffect, useRef, useState } from 'react';

export default function useTimerWindow() {
  const currentWindow = useRef(null);
  const pending = useRef(false);
  const mounted = useRef(true);
  const [timerWindow, setTimerWindow] = useState(null);
  const [windowRoot, setWindowRoot] = useState(null);
  const [windowMode, setWindowMode] = useState(null);
  const [opening, setOpening] = useState(false);
  const [windowError, setWindowError] = useState('');

  const closeTimerWindow = useCallback(() => {
    const target = currentWindow.current;
    currentWindow.current = null;
    if (target && !target.closed) target.close();
    if (mounted.current) {
      setTimerWindow(null);
      setWindowRoot(null);
      setWindowMode(null);
    }
  }, []);

  const openTimerWindow = useCallback(async () => {
    if (currentWindow.current && !currentWindow.current.closed) {
      currentWindow.current.focus();
      return currentWindow.current;
    }
    if (pending.current) return null;
    pending.current = true;
    setOpening(true);
    setWindowError('');
    let target;
    let mode = 'popup';
    try {
      if (window.documentPictureInPicture?.requestWindow) {
        try {
          target = await window.documentPictureInPicture.requestWindow({ width: 340, height: 420 });
          mode = 'picture-in-picture';
        } catch (_error) {
          // A disabled or unavailable PiP API can still allow a regular popup.
        }
      }
      if (!target) target = window.open('', '_blank', 'popup=yes,width=340,height=420');
      if (!target) throw new Error('Window blocked');
      if (!mounted.current) { target.close(); return null; }

      const doc = target.document;
      doc.title = 'Dopatize timer';
      doc.documentElement.lang = document.documentElement.lang || 'en';
      const base = doc.createElement('style');
      base.textContent = 'html,body{margin:0;min-height:100%;background:#090D0E;color-scheme:dark}';
      doc.head.appendChild(base);
      document.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]').forEach((link) => {
        doc.head.appendChild(link.cloneNode(true));
      });
      const root = doc.createElement('div');
      root.id = 'dopatize-timer-window-root';
      doc.body.appendChild(root);
      currentWindow.current = target;
      target.addEventListener('pagehide', () => {
        if (currentWindow.current !== target) return;
        currentWindow.current = null;
        if (mounted.current) { setTimerWindow(null); setWindowRoot(null); setWindowMode(null); }
      }, { once: true });
      setTimerWindow(target);
      setWindowRoot(root);
      setWindowMode(mode);
      return target;
    } catch (_error) {
      if (target && !target.closed) target.close();
      if (mounted.current) setWindowError('Allow pop-ups for Dopatize, then try again.');
      return null;
    } finally {
      pending.current = false;
      if (mounted.current) setOpening(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    window.addEventListener('pagehide', closeTimerWindow);
    return () => {
      mounted.current = false;
      window.removeEventListener('pagehide', closeTimerWindow);
      closeTimerWindow();
    };
  }, [closeTimerWindow]);

  return { timerWindow, windowRoot, windowMode, opening, windowError, openTimerWindow, closeTimerWindow };
}
